import { useState } from 'react';
import type { User } from '../../service/api';

interface UserAvatarProps {
  user: User;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export default function UserAvatar({ 
  user, 
  size = 32, 
  className = '', 
  style = {} 
}: UserAvatarProps) {
  const [imageError, setImageError] = useState(false);

  const getInitials = (givenName: string, familyName: string): string => {
    const firstInitial = givenName.charAt(0).toUpperCase();
    const lastInitial = familyName.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  const getBackgroundColor = (name: string): string => {
    // Generate a consistent color based on the user's name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate HSL color with good contrast
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 45%)`;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const initials = getInitials(user.given_name, user.family_name);
  const backgroundColor = getBackgroundColor(user.given_name + user.family_name);

  if (imageError || !user.picture) {
    return (
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor,
          fontSize: size * 0.4,
          flexShrink: 0,
          ...style
        }}
        title={`${user.given_name} ${user.family_name}`}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={user.picture}
      alt={`${user.given_name} ${user.family_name}`}
      width={size}
      height={size}
      className={`rounded-circle ${className}`}
      style={{ flexShrink: 0, ...style }}
      onError={handleImageError}
      title={`${user.given_name} ${user.family_name}`}
    />
  );
}